import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { GlobalCitizens2023Chapter1 } from '@/components/stories/global-citizens/chapter-1';
import { GlobalCitizens2023Chapter2 } from '@/components/stories/global-citizens/chapter-2';
import { SurveyBackground2023 } from '@/components/survey-background-2023';
import data from '@/data/global-citizens.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as GlobalCitizensData }),
  getStoryProps('global-citizens', 'where-you-call-home')
);

export default function GlobalCitizens(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('global-citizens');
  const pageTitle = t({ message: 'Global citizens', id: 'global-citizens-2023.title' });
  const pageDescription = t({
    id: 'global-citizens-2023.description',
    message: 'Who identifies most with being a citizen of the world?',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.sky} hasBackButton>
        <StoryHeader backgroundId={storySlug} css={{ backgroundPosition: 'center bottom' }}>
          <StoryTheme>
            <Trans id="global-citizens-2023.theme">Globalization</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="global-citizens-2023.tagline">
              <Text type="heading">
                Who identifies most with being a citizen of <span>the world</span>?
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground2023 />

        <GlobalCitizens2023Chapter1 data={data.identify_with_world} />
        <GlobalCitizens2023Chapter2 />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@media screen and (min-aspect-ratio: 1250/900)': {
              marginBlockStart: 'calc(50vh + 25vw)',
            },
          }}
          sharing={{
            title: t({ id: 'global-citizens-2023.sharing.title', message: 'Global citizens' }),
            text: t({
              id: 'global-citizens-2023.sharing.text',
              message: 'Who identifies most with being a citizen of the world? ',
            }),
            url: sharingUrl,
          }}
          relatedStoryId={relatedStoryId}
        >
          <Trans id="global-citizens-2023.closing-question">
            As the world becomes increasingly connected, will the{' '}
            <AnnotatedType>spirit of global citizenship</AnnotatedType> continue to rise with each
            generation?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
